import { useEffect, useMemo, useState } from 'react';

import { Box, Divider, Paper, Skeleton, Typography } from '@mui/material';
import dayjs from 'dayjs';

import httpInstances from '@/api/axiosInstance';
import useUserDateFormat from '@/hooks/useUserDateFormat';
import { FactsheetDetails } from '@/pages/Facthsheets/constants';
import { isValidDateString } from '@/utils/isValidDateString';

const insertSoftBreaks = (text: string) => text.replaceAll(/([ _-])/g, '$1\u200B');

const MapImage = ({ location, zoom }: { location: string; zoom: string }) => {
  const [imageSrc, setImageSrc] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    if (location) {
      const locationUri = `/factsheets/config/location?location=${encodeURI(location.replace(' ', ','))}&zoom=${zoom}&dark=true`;
      httpInstances.axiosInternalInstance
        .get(locationUri, {
          responseType: 'blob'
        })
        .then(response => {
          if (response.status === 200) {
            const imgUrl = URL.createObjectURL(response.data);
            setImageSrc(imgUrl);
          } else setError('Unable to get map image');
        })
        .catch(error => {
          console.error('Error fetching image:', error);
          setError(`Error fetching image:${error}`);
        });
    }
  }, [location, zoom]);

  if (!error) {
    return (
      <Box sx={{ width: '40%' }}>
        {imageSrc ? (
          <img
            alt='factsheetImage'
            src={imageSrc}
            style={{ aspectRatio: 1, width: '100%' }}
          />
        ) : (
          <Skeleton
            sx={{ aspectRatio: 1, width: '100%', height: 'auto' }}
            variant='rectangular'
          />
        )}
      </Box>
    );
  }
};

const Tile = ({
  label,
  value
}: {
  label: string;
  value: string | boolean | string[] | undefined;
}) => {
  if (value)
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 1.5,
          overflow: 'hidden',
          minWidth: 180,
          maxWidth: 180,
          wordBreak: 'break-word'
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
        {Array.isArray(value) ? (
          value.map((item, index) => (
            <Typography
              key={item + index}
              sx={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word'
              }}
            >
              {insertSoftBreaks(item)}
            </Typography>
          ))
        ) : (
          <Typography
            sx={{
              whiteSpace: 'pre-wrap',
              overflowWrap: 'break-word'
            }}
          >
            {insertSoftBreaks(value.toString())}
          </Typography>
        )}
      </Box>
    );
};

const DetailsTab = ({
  factsheet,
  gxToken
}: {
  factsheet: FactsheetDetails;
  gxToken: string | null;
}) => {
  const dateFormat = useUserDateFormat();

  const startDate = useMemo(() => {
    const date = factsheet.indexDetails['startDate'] as string;

    if (isValidDateString(date)) {
      return dayjs(date).format(dateFormat.value.toUpperCase());
    }
    return date;
  }, [dateFormat, factsheet.indexDetails]);

  const endDate = useMemo(() => {
    const date = factsheet.indexDetails['endDate'] as string | undefined;
    if (!date?.length) return '(ongoing)';
    if (isValidDateString(date)) {
      return dayjs(date).format(dateFormat.value.toUpperCase());
    }
    return date;
  }, [dateFormat, factsheet.indexDetails]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
      <Box sx={{ flex: 1 }}>
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start'
          }}
        >
          <Tile
            label='Currency / Units'
            value={`${factsheet.indexDetails['currency']} / ${factsheet.indexDetails['units']}`}
          />
          <Tile
            label='Frequency'
            value={factsheet.indexDetails['frequency']}
          />
          <Tile
            label='Pricing Basis'
            value={factsheet.indexDetails['pricingBasis']}
          />
          <Tile
            label='Timezone'
            value={factsheet.indexDetails['timeZone']}
          />
          <Tile
            label='Trading Hub'
            value={factsheet.indexDetails['tradingHub']}
          />
          <Tile
            label='Delivery Basis'
            value={factsheet.indexDetails['deliveryBasis']}
          />
          <Tile
            label='Commodity'
            value={factsheet.indexDetails['commodity']}
          />
          <Tile
            label='Index Module(s)'
            value={factsheet.indexDetails['indexModules']}
          />
          <Tile
            label='Holiday Calendar'
            value={factsheet.indexDetails['holidayCalendar']}
          />
          <Tile
            label='Last Trade Calendar'
            value={factsheet.indexDetails['lastTradeCalendar']}
          />
          <Tile
            label='Columns Available'
            value={factsheet.indexDetails['columnsAvailable']}
          />
          <Tile
            label='Increment'
            value={factsheet.indexDetails['increment']}
          />
        </Paper>
        <Divider sx={{ backgroundColor: 'secondary.main', pr: 4 }} />
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start'
          }}
        >
          <Tile
            label='Status'
            value={factsheet.indexDetails['status']}
          />
          <Tile
            label='Start Date'
            value={startDate}
          />
          <Tile
            label='End Date'
            value={endDate}
          />
        </Paper>
        <Divider sx={{ backgroundColor: 'secondary.main', pr: 4 }} />
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start'
          }}
        >
          <Tile
            label='Spot Alias'
            value={factsheet.indexDetails['spotAlias'] || '(none)'}
          />
          <Tile
            label='Forward Curve'
            value={factsheet.indexDetails['forwardCurve'] ? 'Yes' : 'No'}
          />
          <Tile
            label='Num Items'
            value={factsheet.symbols?.length.toString()}
          />
        </Paper>
      </Box>
      {gxToken && factsheet.mapLocation && (
        <MapImage
          location={factsheet.mapLocation}
          zoom={factsheet.mapZoom ?? '10'}
        />
      )}
    </Box>
  );
};
export default DetailsTab;
